import React from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';

const SEO = ({
  title,
  titleTemplate = "%s",
  description = "Le Poney Club est de retour à Toulouse-Blagnac en 2025 pour une saison exceptionnelle. Découvrez des artistes internationaux, des styles variés et des formules adaptées à tous.",
  image = '/poneyseo.png',
  url = "/",
  type = "website",
  locale = "fr_FR",
  keywords = [],
  author = "Poney Club Toulouse 2025",
  twitterHandle = "@PoneyFestival",
  noindex = false,
  structuredData = null,
}) => {
  const siteUrl = process.env.NODE_ENV === 'production'
    ? "https://www.poneyclubtoulouse.fr"
    : "https://192.168.1.103:3000";

  const canonicalUrl = `${siteUrl}${url.startsWith('/') ? url : `/${url}`}`;
  const imageUrl = image.startsWith('http')
  ? image
  : `${siteUrl}${image.startsWith('/') ? image : `/${image}`}`;

  const finalTitle = title
    ? titleTemplate.replace("%s", title)
    : "Poney Club | 11 Juin > 3O Août 2025 | Toulouse - France";

  // Données structurées par défaut pour la page web
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: finalTitle,
    description,
    startDate: "2025-06-11", // Date de début de la saison
    endDate: "2025-08-31", // Date de fin estimée
    location: {
      "@type": "Place",
      name: "Aéroport de Toulouse-Blagnac",
      address: {
        "@type": "PostalAddress",
        streetAddress: "31703 Toulouse",
        addressLocality: "Toulouse",
        addressRegion: "Occitanie",
        postalCode: "31703",
        addressCountry: "FR"
      }
    },
    image: imageUrl,
    url: canonicalUrl,
    organizer: {
      "@type": "Organization",
      name: "Poney Club Toulouse 2025",
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/logo.png`
      }
    }
  };

  let finalStructuredData = defaultStructuredData;

  if (structuredData) {
    if (Array.isArray(structuredData)) {
      finalStructuredData = structuredData;
    } else {
      finalStructuredData = { ...defaultStructuredData, ...structuredData };
    }
  }

  const generateStructuredDataScripts = () => {
    if (Array.isArray(finalStructuredData)) {
      return finalStructuredData.map((data, index) => (
        <script
          key={`structured-data-${index}`}
          type="application/ld+json"
        >
          {JSON.stringify(data)}
        </script>
      ));
    }

    return (
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    );
  };

  const autoKeywords = [
    "Poney Club Toulouse 2025",
    "Festival à Toulouse",
    "Open Air Toulouse",
    "Musique live Toulouse",
    "Carl Cox",
    "Boris Brejcha",
    "The Blaze",
    "Petit Biscuit",
    "Parov Stelar",
    ...keywords
  ];

  return (
    <Helmet>
      {/* Titre et URL canonique */}
      <title>{finalTitle}</title>
      <link rel="canonical" href={canonicalUrl} />

      {/* Préconnect pour optimiser le chargement des ressources */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

      {/* Favicons */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
      <meta name="msapplication-TileColor" content="#da532c" />
      <meta name="theme-color" content="#ffffff" />

      {/* Métadonnées SEO */}
      <meta name="description" content={description} />
      <meta name="author" content={author} />
      <meta name="keywords" content={autoKeywords.join(", ")} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content="Poney Club Toulouse 2025" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Données structurées (Schema.org) */}
      {generateStructuredDataScripts()}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string.isRequired,
  titleTemplate: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  url: PropTypes.string,
  type: PropTypes.string,
  locale: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string),
  author: PropTypes.string,
  twitterHandle: PropTypes.string,
  noindex: PropTypes.bool,
  structuredData: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
};

export default SEO;
